import { useState, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MapPin, Phone, Printer, Mail, Clock, Linkedin, UploadCloud, X, ArrowRight } from "lucide-react";
import PageHero from "@/components/PageHero";
import { Reveal, Label } from "@/components/Reveal";
import CTASection from "@/components/CTASection";
import { IMAGES, COMPANY, SERVICE_OPTIONS, PROJECT_TYPES } from "@/lib/data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX = 25 * 1024 * 1024;

const InfoRow = ({ icon: Icon, children }) => (
  <div className="flex items-start gap-4">
    <div className="w-10 h-10 border border-border flex items-center justify-center shrink-0 text-brand">
      <Icon className="w-5 h-5" strokeWidth={1.5} />
    </div>
    <div className="text-navy/80 text-sm leading-relaxed pt-2">{children}</div>
  </div>
);

const initial = {
  full_name: "", company: "", email: "", phone: "",
  project_location: "", service_needed: "", project_type: "", project_description: "",
};

export default function Contact() {
  const [form, setForm] = useState(initial);
  const [files, setFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const fileRef = useRef(null);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const addFiles = (list) => {
    const arr = Array.from(list);
    const valid = [];
    for (const f of arr) {
      if (!f.name.toLowerCase().endsWith(".pdf")) { toast.error(`${f.name}: PDF files only`); continue; }
      if (f.size > MAX) { toast.error(`${f.name}: exceeds 25MB`); continue; }
      valid.push(f);
    }
    setFiles((prev) => [...prev, ...valid]);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.email || !form.service_needed) {
      toast.error("Please fill in all required fields.");
      return;
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => v && fd.append(k, v));
      files.forEach((f) => fd.append("files", f));
      const { data } = await axios.post(`${API}/contact`, fd);
      toast.success(data.message || "Request submitted!");
      setForm(initial);
      setFiles([]);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls = "w-full border border-border px-4 py-3 text-sm text-navy bg-white focus:border-brand outline-none transition-colors placeholder:text-navy/30";
  const labelCls = "block text-sm font-semibold text-navy mb-2";

  return (
    <>
      <PageHero
        label="Contact Us"
        titleLines={["Let's build", "better together."]}
        description="We'd love to hear about your project and explore how we can help you achieve high performance building outcomes."
        image={IMAGES.contactHero}
        imageAlt="Vancouver waterfront"
      />

      <section className="bg-white" data-testid="contact-section">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-24 grid lg:grid-cols-[1fr_1.5fr] gap-16">
          {/* Info column */}
          <Reveal>
            <Label>Contact Information</Label>
            <h2 className="font-heading text-2xl uppercase text-navy font-semibold mt-4 mb-8">{COMPANY.name}</h2>
            <div className="space-y-6">
              <InfoRow icon={MapPin}>{COMPANY.address.map((l, i) => <div key={i}>{l}</div>)}</InfoRow>
              <InfoRow icon={Phone}>{COMPANY.phone}</InfoRow>
              <InfoRow icon={Printer}>Fax {COMPANY.fax}</InfoRow>
              <InfoRow icon={Mail}><a href={`mailto:${COMPANY.email}`} className="hover:text-brand">{COMPANY.email}</a></InfoRow>
            </div>
            <div className="border-t border-border my-8" />
            <Label>Office Hours</Label>
            <div className="mt-4">
              <InfoRow icon={Clock}>{COMPANY.hours.map((h, i) => <div key={i}>{h}</div>)}</InfoRow>
            </div>
            <div className="border-t border-border my-8" />
            <Label>Connect</Label>
            <div className="flex gap-3 mt-4">
              <a href={COMPANY.linkedin} target="_blank" rel="noreferrer" data-testid="contact-linkedin"
                 className="w-11 h-11 bg-navy text-white flex items-center justify-center hover:bg-brand transition-colors">
                <Linkedin className="w-5 h-5" />
              </a>
              <a href={`mailto:${COMPANY.email}`} data-testid="contact-email-icon"
                 className="w-11 h-11 bg-navy text-white flex items-center justify-center hover:bg-brand transition-colors">
                <Mail className="w-5 h-5" />
              </a>
            </div>
          </Reveal>

          {/* Form column */}
          <Reveal delay={0.15}>
            <Label>Send Us A Message</Label>
            <p className="text-navy/60 mt-3 mb-8">Please fill out the form below and a member of our team will get back to you as soon as possible.</p>
            <form onSubmit={submit} data-testid="contact-form" className="space-y-6">
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label className={labelCls}>Full Name <span className="text-brand">*</span></label>
                  <input className={inputCls} placeholder="Your name" value={form.full_name} onChange={set("full_name")} data-testid="input-full-name" />
                </div>
                <div>
                  <label className={labelCls}>Company</label>
                  <input className={inputCls} placeholder="Your company" value={form.company} onChange={set("company")} data-testid="input-company" />
                </div>
                <div>
                  <label className={labelCls}>Email Address <span className="text-brand">*</span></label>
                  <input type="email" className={inputCls} placeholder="you@example.com" value={form.email} onChange={set("email")} data-testid="input-email" />
                </div>
                <div>
                  <label className={labelCls}>Phone Number</label>
                  <input className={inputCls} placeholder="(604) 123-4567" value={form.phone} onChange={set("phone")} data-testid="input-phone" />
                </div>
              </div>

              <div>
                <label className={labelCls}>Project Location</label>
                <input className={inputCls} placeholder="City, BC" value={form.project_location} onChange={set("project_location")} data-testid="input-location" />
              </div>

              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label className={labelCls}>Service Needed <span className="text-brand">*</span></label>
                  <select className={inputCls} value={form.service_needed} onChange={set("service_needed")} data-testid="select-service">
                    <option value="">Select a service</option>
                    {SERVICE_OPTIONS.map((s) => <option key={s}>{s}</option>)}
                  </select>
                </div>
                <div>
                  <label className={labelCls}>Project Type</label>
                  <select className={inputCls} value={form.project_type} onChange={set("project_type")} data-testid="select-type">
                    <option value="">Select project type</option>
                    {PROJECT_TYPES.map((t) => <option key={t}>{t}</option>)}
                  </select>
                </div>
              </div>

              <div>
                <label className={labelCls}>Project Description</label>
                <textarea rows={4} className={inputCls} placeholder="Tell us about your project" value={form.project_description} onChange={set("project_description")} data-testid="input-description" />
              </div>

              <div>
                <label className={labelCls}>Upload Drawings / Documents</label>
                <div
                  onClick={() => fileRef.current?.click()}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={(e) => { e.preventDefault(); addFiles(e.dataTransfer.files); }}
                  className="border border-dashed border-border p-8 flex flex-col items-center justify-center gap-2 cursor-pointer hover:border-brand transition-colors text-center"
                  data-testid="file-dropzone"
                >
                  <UploadCloud className="w-7 h-7 text-brand" strokeWidth={1.5} />
                  <p className="text-sm text-navy/70"><span className="font-semibold">Drag and drop files here</span> or click to browse</p>
                  <p className="text-xs text-navy/40">PDF files only, max 25MB</p>
                  <input ref={fileRef} type="file" accept=".pdf" multiple hidden onChange={(e) => addFiles(e.target.files)} data-testid="file-input" />
                </div>
                {files.length > 0 && (
                  <ul className="mt-3 space-y-2" data-testid="file-list">
                    {files.map((f, i) => (
                      <li key={i} className="flex items-center justify-between bg-secondary px-4 py-2 text-sm">
                        <span className="text-navy truncate">{f.name}</span>
                        <button type="button" onClick={() => setFiles((p) => p.filter((_, idx) => idx !== i))} className="text-navy/50 hover:text-destructive">
                          <X className="w-4 h-4" />
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <button
                type="submit"
                disabled={submitting}
                data-testid="submit-button"
                className="group inline-flex items-center gap-3 bg-brand text-white px-10 py-4 text-xs tracking-[0.18em] uppercase font-bold hover:bg-navy transition-colors disabled:opacity-60"
              >
                {submitting ? "Submitting..." : "Submit Request"}
                {!submitting && <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />}
              </button>
            </form>
          </Reveal>
        </div>
      </section>

      <CTASection />
    </>
  );
}
